/**
 * Quantity change event handlers with inventory validation
 * @module shared-cart/events/quantity
 */

import { SHOPIFY_MANAGED_VENDORS, UserMessages } from '../constants.js';
import { displayError } from '../ui/error.js';
import { removeCartItem } from '../cart/api.js';
import { getInventories, checkItemAvailability } from '../external/inventory.js';

interface QuantityContext {
  button: HTMLButtonElement;
  input: HTMLInputElement;
  variantId: string;
  sku: string;
  vendor: string;
}

type QuantityAction = 'increment' | 'decrement';
type QuantityEvent = Pick<MouseEvent, 'target' | 'preventDefault'> &
  Partial<Pick<MouseEvent, 'stopPropagation' | 'stopImmediatePropagation'>>;
type PubSubWindow = Window & {
  subscribe?: (event: string, callback: (data: unknown) => void) => unknown;
};

/**
 * @typedef {Object} QuantityContext
 * @property {HTMLButtonElement} button - The quantity button clicked
 * @property {HTMLInputElement} input - The quantity input element
 * @property {string} variantId - The variant ID
 * @property {string} sku - The product SKU
 * @property {string} vendor - The product vendor
 */

/**
 * Extracts context from a quantity button click event
 * @param {MouseEvent} event - The click event
 * @returns {QuantityContext|null} Context object or null if not a quantity button
 */
export function extractQuantityContext(event: Pick<MouseEvent, 'target'>): QuantityContext | null {
  const button = (event.target as Element).closest<HTMLButtonElement>('.quantity__button');
  if (!button) return null;

  const input = button.parentElement!.querySelector<HTMLInputElement>('.quantity__input');
  if (!input) return null;

  const row = input.closest<HTMLElement>('.cart-item') || input.closest<HTMLTableRowElement>('tr');
  if (!row) return null;

  const variantId = row.dataset.variantId;
  const sku = row.dataset.sku;
  const vendor = row.dataset.vendor;

  if (!variantId || !sku || !vendor) return null;

  return { button, input, variantId, sku, vendor };
}

/**
 * Checks if a vendor's inventory is managed by Shopify
 * @param {string} vendor - The vendor name
 * @returns {boolean} True if Shopify-managed
 */
export function isShopifyManagedVendor(vendor: string | null | undefined): boolean {
  return SHOPIFY_MANAGED_VENDORS.includes(vendor?.toLowerCase() as string);
}

/**
 * Calculates the new quantity after increment/decrement
 * @param {HTMLInputElement} input - The quantity input element
 * @param {'increment'|'decrement'} action - The action to perform
 * @returns {number} The new quantity value
 */
export function calculateNewQuantity(input: HTMLInputElement, action: QuantityAction): number {
  const currentValue = parseInt(input.value, 10);
  return action === 'increment' ? currentValue + 1 : currentValue - 1;
}

/**
 * Applies a quantity change to the input element
 * @param {HTMLInputElement} input - The quantity input element
 * @param {'increment'|'decrement'} action - The action to perform
 */
export function applyQuantityChange(input: HTMLInputElement, action: QuantityAction): void {
  if (action === 'increment') {
    input.stepUp();
  } else {
    input.stepDown();
  }
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Handles quantity button click with inventory validation
 * @param {MouseEvent} event - The click event
 * @returns {Promise<void>}
 */
export async function handleQuantityChange(event: QuantityEvent): Promise<boolean | void> {
  const context = extractQuantityContext(event);
  if (!context) return;

  const { button, input, variantId, sku, vendor } = context;
  const dispatchChange = () => input.dispatchEvent(new Event('change', { bubbles: true }));

  // Skip validation for Shopify-managed vendors
  if (isShopifyManagedVendor(vendor)) {
    return dispatchChange();
  }

  // Stop event propagation for external vendor products
  event.preventDefault();
  event.stopPropagation!();
  event.stopImmediatePropagation!();

  const action = button.name === 'plus' ? 'increment' : 'decrement';
  const newQuantity = calculateNewQuantity(input, action);

  console.info(`[Shared Cart] Quantity ${action} requested for ${sku}:`, {
    newQuantity,
  });

  // Allow decrements to 0 (removes item) without inventory check
  if (newQuantity === 0) {
    applyQuantityChange(input, action);
    return;
  }

  // Perform inventory check for external-managed products
  try {
    const inventories = await getInventories([sku]);
    const item = { sku, id: variantId, quantity: newQuantity };
    const isAvailable = checkItemAvailability(item, inventories);

    if (isAvailable) {
      applyQuantityChange(input, action);
    } else {
      console.warn(`[Shared Cart] Item ${sku} not available, removing from cart`);
      await removeCartItem(variantId);
      displayError(UserMessages.OUT_OF_STOCK);
    }
  } catch (error) {
    console.error('[Shared Cart] Error during quantity change validation:', error);
  }
}

/**
 * Initializes cart quantity button listeners with inventory validation
 */
export function initCartListeners(): void {
  console.info('[Shared Cart] Initializing cart listeners');

  // Use capture phase to intercept before Dawn theme handlers
  document.body.addEventListener('click', handleQuantityChange, {
    capture: true,
  });

  // Subscribe to cart update events if pub/sub is available
  const pubSubWindow = window as PubSubWindow;
  if (pubSubWindow.subscribe && window.PUB_SUB_EVENTS) {
    pubSubWindow.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (data) => {
      console.info('[Shared Cart] Cart update completed:', data);
    });
  }
}
